import timeInfo from "../../../utils/timeInfo";
import React, { useEffect, useState } from "react";
import { ref, onValue, update, set } from "firebase/database";
import { database, firestore } from "../../../utils/firebase";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import {
    Autocomplete,
    Breadcrumbs,
    Input,
    Link,
    Typography,
    Button,
    Table,
    Avatar,
    Dropdown,
    MenuButton,
    Menu,
    MenuItem,
    ListItemDecorator,
    Modal,
    ModalDialog,
    Stack,
    FormControl,
    FormLabel,
    DialogTitle,
    Select,
    Option
} from "@mui/joy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForever from "@mui/icons-material/DeleteForever";
import {MoreVert, PersonAdd} from "@mui/icons-material";
import VerifiedIcon from '@mui/icons-material/Verified';
import md5 from 'crypto-js/md5';



const LeagueSettings = () => {
    const { leagueId } = useParams();
    const { timeZones = [], localeCodes = [] } = timeInfo;
    const leagueRef = ref(database, `leagues/${leagueId}`);
    const [leagueData, setLeagueData] = useState({});
    const [subscriptionData, setSubscriptionData] = useState({});
    const tiers = ['Trial', 'Basic', 'Advanced', 'Community', 'Enterprise'];
    const navigate = useNavigate();
    const [timeZone, setTimeZone] = useState('');
    const [timezoneInputValue, setTimezoneInputValue] = useState('');
    const [language, setLanguage] = useState('');
    const [languageInputValue, setLanguageInputValue] = useState('');
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [inviteRole, setInviteRole] = useState(2);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (unsavedChanges) {
                event.preventDefault();
                event.returnValue = "You have unsaved changes. Do you really want to leave?";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [unsavedChanges]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (unsavedChanges) {
                event.preventDefault();
                event.returnValue = "You have unsaved changes. Do you really want to leave?";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [unsavedChanges]);

    useEffect(() => {
        onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            setLeagueData(data || {});
            setTimeZone(data?.settings?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
            setTimezoneInputValue(data?.settings?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
            setLanguage(data?.settings?.language || navigator.language);
            setLanguageInputValue(data?.settings?.language || navigator.language);
        });
    }, [leagueId]);

    useEffect(() => {
        const fetchSubscriptionData = async () => {
            const docRef = doc(firestore, `billing/${leagueId}`);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setSubscriptionData(docSnap.data());
            }
        };
        fetchSubscriptionData();
    }, [leagueId]);

    const handleFieldChange = (field, value) => {
        if (field === "timeZone" || field === "language") {
            setLeagueData(prev => ({
                ...prev,
                settings: {
                    ...prev.settings,
                    [field]: value
                }
            }));
        } else {
            setLeagueData(prev => ({ ...prev, [field]: value }));
        }
        setUnsavedChanges(true);
    };


    const handleSave = () => {
        const updatedData = {
            ...leagueData,
            settings: {
                timeZone,
                language,
                ...leagueData.settings
            }
        };
        update(leagueRef, updatedData);
        setUnsavedChanges(false);
    };

    const handleInvite = (email) => {
        const inviteRef = ref(database, `invites/${email.replace(/\./g, ",")}/${leagueId}`);
        set(inviteRef, Number(inviteRole)); // Directly saving role under email/leagueId
        const adminRef = ref(database, `leagues/${leagueId}/admins/${email.replace(/\./g, ",")}`);
        set(adminRef, {
            name: email,  // Replace with the actual variable holding the user's name
            email: email,
            role: Number(inviteRole)
        });
    };

    const handleChangeAccess = (adminKey, newRole) => {
        const adminRef = ref(database, `leagues/${leagueId}/admins/${adminKey}`);
        update(adminRef, { role: newRole });
    };

    const handleRevokeAccess = (adminKey) => {
        const adminRef = ref(database, `leagues/${leagueId}/admins/${adminKey}`);
        const email = leagueData.admins[adminKey]?.email;
        if (email) {
            const inviteRef = ref(database, `invites/${email.replace(/\./g, ",")}/${leagueId}`);
            set(inviteRef, null);
        }
        set(adminRef, null);
    };

    const handleInviteSubmit = (event) => {
        event.preventDefault();
        const email = event.target.email.value;
        handleInvite(email);
        setInviteModalOpen(false);
    };

    const getGravatarUrl = (email) => {
        if (!email) return "https://www.gravatar.com/avatar/?d=mp"; // Fallback for missing emails
        const hash = md5(email.trim().toLowerCase()).toString();
        return `https://www.gravatar.com/avatar/${hash}?s=200&d=mp`; // Fetch user's Gravatar
    };





    return (
        <div className="container">
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    {leagueData.name}
                </Link>
                <Typography sx={{ color: 'text.primary' }}>Settings</Typography>
            </Breadcrumbs>
            <h1>League Settings</h1>
            <div className="dashboard">
                <h2>Subscription Information</h2>
                <div>
                    <p>Plan: {tiers[subscriptionData.plan]}</p>
                    <p>Renews: {subscriptionData.expiration ? new Date(subscriptionData.expiration * 1000).toLocaleDateString() : "N/A"}</p>
                    <p>Active: {subscriptionData.active ? "Yes" : "No"}</p>
                </div>
                <h2>League Info</h2>
                <div>
                    <label>
                        Name:
                        <Input
                            type="text"
                            value={leagueData.name || ''}
                            onChange={(e) => handleFieldChange("name", e.target.value)}
                        />
                    </label>
                    <label>
                        Location:
                        <Input
                            type="text"
                            value={leagueData.parentGroup || ''}
                            onChange={(e) => handleFieldChange("parentGroup", e.target.value)}
                        />
                    </label>
                </div>
                <h2>Time Zone & Language</h2>
                <div style={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
                    <Autocomplete
                        value={leagueData.settings?.timeZone || ''}
                        onChange={(event, newValue) => handleFieldChange("timeZone", newValue)}
                        inputValue={timezoneInputValue}
                        onInputChange={(event, newInputValue) => setTimezoneInputValue(newInputValue)}
                        options={timeZones}
                    />

                    <Autocomplete
                        value={leagueData.settings?.language || ''}
                        onChange={(event, newValue) => handleFieldChange("language", newValue)}
                        inputValue={languageInputValue}
                        onInputChange={(event, newInputValue) => setLanguageInputValue(newInputValue)}
                        options={localeCodes}
                    />
                </div>
                <Button onClick={handleSave} disabled={!unsavedChanges} sx={{ mt: 2 }}>
                    Save Changes
                </Button>
                <h2>Access</h2>
                <Table style={{ width: '600px' }}>
                    <thead>
                    <tr>
                        <th width={"40px"}></th>
                        <th width={"200px"}>Name</th>
                        <th width={"100px"}>Access</th>
                        <th width={"50px"}></th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.entries(leagueData.admins || {}).map(([key, admin]) => {
                        const email = admin.email || key.replace(/,/g, "."); // Retrieve email if stored or reconstruct from key
                        return (
                            <tr key={key}>
                                <Avatar src={admin.app ? admin.imageUrl : getGravatarUrl(email)} alt={admin.name || "User"}>
                                    {admin.name ? admin.name.charAt(0) : "?"}
                                </Avatar>
                                <td>{admin.name || "Unknown"}
                                    {admin.verified && (<VerifiedIcon color={"primary"}/>)}
                                </td>
                                <td>
                                    {admin.app? "App" :
                                        admin.role === 0 ? "Owner" :
                                            admin.role === 1 ? "Administrator" :
                                                "Editor"

                                    }

                                </td>
                                <td>
                                    <Dropdown>
                                        <MenuButton variant={"plain"} sx={{width: "15px"}}>
                                            <MoreVert/>
                                        </MenuButton>
                                        <Menu placement="bottom-end">
                                            <MenuItem onClick={() => handleChangeAccess(key, 2)}>
                                                <ListItemDecorator sx={{ color: 'inherit' }}>
                                                    <ContentCopyIcon />
                                                </ListItemDecorator>{" "}
                                                Change Access
                                            </MenuItem>
                                            <MenuItem variant="soft" color="danger" onClick={() => handleRevokeAccess(key)}>
                                                <ListItemDecorator sx={{ color: 'inherit' }}>
                                                    <DeleteForever />
                                                </ListItemDecorator>{" "}
                                                Revoke Access
                                            </MenuItem>
                                        </Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </Table>
                <br/>
                <Button className={"button-gradient"} onClick={() => setInviteModalOpen(true)}><PersonAdd style={{paddingRight: "10px"}}/>Invite</Button>
            </div>
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                <ModalDialog>
                    <DialogTitle>Edit Access</DialogTitle>
                    <form>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Level</FormLabel>
                                <Select>
                                    <Option value="0">Owner</Option>
                                    <Option value="1">Administrator</Option>
                                    <Option value="2">Editor</Option>
                                </Select>
                            </FormControl>
                            <Button type="submit">Save</Button>
                        </Stack>
                    </form>
                </ModalDialog>
            </Modal>
            <Modal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)}>
                <ModalDialog>
                    <DialogTitle>Invite</DialogTitle>
                    <form onSubmit={handleInviteSubmit}>
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Email</FormLabel>
                                <Input type="email" name="email" required />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Role</FormLabel>
                                <Select name="role" value={inviteRole} onChange={(event, value) => setInviteRole(value)} required>
                                    <Option value="0">Owner</Option>
                                    <Option value="1">Administrator</Option>
                                    <Option value="2">Editor</Option>
                                </Select>
                            </FormControl>
                            <Button type="submit">Invite</Button>
                        </Stack>
                    </form>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default LeagueSettings;
